import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API, absUrl, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import QrModal from "@/components/QrModal";
import NfcModal from "@/components/NfcModal";
import { toast } from "sonner";
import {
  Plus, Search, Nfc, Building2, Pencil, Copy, ExternalLink, QrCode, UserPlus,
  Trash2, LogOut, Download, Wifi, Loader2,
} from "lucide-react";

const STATUS_STYLES = {
  Bozza: "bg-amber-100 text-amber-800 border-amber-300",
  Pronta: "bg-blue-100 text-blue-800 border-blue-300",
  "NFC scritto": "bg-indigo-100 text-indigo-800 border-indigo-300",
  Spedita: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [qrCard, setQrCard] = useState(null);
  const [nfcCard, setNfcCard] = useState(null);
  const [deleteCard, setDeleteCard] = useState(null);

  const load = useCallback(async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/cards", { params: { q: search } });
      setCards(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  const doDuplicate = async (id) => {
    try {
      await api.post(`/cards/${id}/duplicate`);
      toast.success("Carta duplicata");
      load(q);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async () => {
    const id = deleteCard.id;
    setDeleteCard(null);
    try {
      await api.delete(`/cards/${id}`);
      toast.success("Carta eliminata");
      load(q);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doExport = async (fmt) => {
    try {
      const res = await api.get(`/cards/export/${fmt}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `clienti_nfc.${fmt}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Export ${fmt.toUpperCase()} scaricato`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header data-testid="admin-header" className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-8 py-3.5 flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-md shadow-indigo-500/30">
              <Nfc className="text-white" size={20} />
            </div>
            <div className="leading-tight">
              <p className="font-display font-extrabold text-slate-900">TapCard Manager</p>
              <p className="text-[11px] text-slate-400">{cards.length} clienti</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button data-testid="button-export-csv" variant="outline" size="sm" onClick={() => doExport("csv")} className="hidden sm:flex">
              <Download size={15} className="mr-1.5" /> CSV
            </Button>
            <Button data-testid="button-export-json" variant="outline" size="sm" onClick={() => doExport("json")} className="hidden sm:flex">
              <Download size={15} className="mr-1.5" /> JSON
            </Button>
            <span className="hidden md:block text-xs text-slate-500 max-w-[180px] truncate">{user?.email}</span>
            <Button data-testid="btn-logout" variant="ghost" size="icon" onClick={logout}><LogOut size={18} /></Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-4 sm:px-8 py-8">
        {/* Hero row */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Le tue carte NFC</h1>
            <p className="mt-1 text-slate-500">Crea, modifica e gestisci le carte digitali dei clienti.</p>
          </div>
          <Button data-testid="button-new-card" onClick={() => navigate("/card/new")}
            className="h-14 px-7 text-base font-semibold bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/25 rounded-2xl">
            <Plus size={22} className="mr-2" /> Nuova carta NFC
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-xl">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="input-search-cards" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome, azienda, email, telefono..." className="h-12 pl-11 rounded-2xl bg-white" />
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" size={32} /></div>
        ) : cards.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white py-20 text-center">
            <Building2 className="mx-auto mb-3 text-slate-300" size={44} />
            <p className="font-semibold text-slate-700">Nessuna carta ancora</p>
            <p className="mt-1 text-sm text-slate-400">Clicca "Nuova carta NFC" per iniziare.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {cards.map((c) => (
              <div key={c.id} data-testid={`card-item-${c.id}`}
                className="group rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3.5">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-2xl bg-slate-100 flex items-center justify-center ring-1 ring-slate-200">
                    {c.logo_url ? <img src={absUrl(c.logo_url)} alt="" className="h-full w-full object-cover" /> : <Building2 className="text-slate-300" size={22} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-slate-900 truncate">{c.company_name || "Senza nome"}</p>
                    <p className="text-sm text-slate-500 truncate">{c.full_name || c.profession || "—"}</p>
                    <Badge variant="outline" className={`mt-1.5 text-[11px] font-semibold ${STATUS_STYLES[c.status] || ""}`}>{c.status}</Badge>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-1.5">
                  <ActionIcon testId={`btn-edit-card-${c.id}`} onClick={() => navigate(`/card/${c.id}`)} icon={Pencil} label="Modifica" />
                  <ActionIcon testId={`btn-duplicate-card-${c.id}`} onClick={() => doDuplicate(c.id)} icon={Copy} label="Duplica" />
                  <ActionIcon testId={`btn-open-page-${c.id}`} onClick={() => window.open(c.public_url, "_blank")} icon={ExternalLink} label="Apri" />
                  <ActionIcon testId={`btn-qr-modal-${c.id}`} onClick={() => setQrCard(c)} icon={QrCode} label="QR" />
                  <ActionIcon testId={`btn-vcard-${c.id}`} onClick={() => window.open(`${API}/public/cards/${c.id}/vcard`, "_blank")} icon={UserPlus} label="Contatto" />
                  <ActionIcon testId={`btn-nfc-write-${c.id}`} onClick={() => setNfcCard(c)} icon={Wifi} label="NFC" highlight />
                </div>
                <button data-testid={`btn-delete-card-${c.id}`} onClick={() => setDeleteCard(c)}
                  className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-medium text-red-500 hover:bg-red-50 transition-colors">
                  <Trash2 size={14} /> Elimina
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      <QrModal card={qrCard} open={!!qrCard} onOpenChange={(v) => !v && setQrCard(null)} />
      <NfcModal card={nfcCard} open={!!nfcCard} onOpenChange={(v) => !v && setNfcCard(null)} onWritten={() => { setNfcCard(null); load(q); }} />

      <AlertDialog open={!!deleteCard} onOpenChange={(v) => !v && setDeleteCard(null)}>
        <AlertDialogContent data-testid="dialog-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa carta?</AlertDialogTitle>
            <AlertDialogDescription>
              La carta "{deleteCard?.company_name}" e il suo URL pubblico verranno eliminati definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-confirm-delete" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ActionIcon({ testId, onClick, icon: Icon, label, highlight }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`flex flex-col items-center gap-1 rounded-xl py-2.5 text-[11px] font-medium transition-colors ${
        highlight ? "bg-indigo-50 text-indigo-600 hover:bg-indigo-100" : "text-slate-600 hover:bg-slate-100"
      }`}>
      <Icon size={17} /> {label}
    </button>
  );
}
